import React from "react";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

const CustomBox = styled(Box)(({ topColor, bottomColor }) => ({
  display: "flex",
  flexDirection: "column",
  width: "150px",
  height: "65px",
  borderRadius: "12px",
  border: "1px solid grey",
  overflow: "hidden",
  boxShadow: "none",
  color: "#fff",
  position: "relative",
  backgroundColor: topColor,
  cursor: "pointer",
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: "55%",
    backgroundColor: bottomColor,
  },
  "& > div": {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    zIndex: 1,
    position: "relative",
    padding: "10px",
    textAlign: "center",
  },
  "& .label": {
    fontSize: "1rem",
    fontWeight: 600,
    color: "black",
    marginBottom: "15px",
  },
  "& .count": {
    fontSize: "1rem",
    fontWeight: 800,
    marginBottom: "4px",
  },
}));

const polarToCartesian = (cx, cy, radius, angleInDegrees) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(angleInRadians),
    y: cy + radius * Math.sin(angleInRadians),
  };
};

const describeSlice = (cx, cy, radius, startAngle, endAngle) => {
  const angle = endAngle - startAngle;

  // SVG arcs cannot draw a full 360° circle (start === end collapses the path).
  // Use two semicircles so a 100% slice still renders as a full pie.
  if (angle >= 359.999) {
    return [
      `M ${cx} ${cy - radius}`,
      `A ${radius} ${radius} 0 1 1 ${cx} ${cy + radius}`,
      `A ${radius} ${radius} 0 1 1 ${cx} ${cy - radius}`,
      "Z",
    ].join(" ");
  }

  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArcFlag = angle <= 180 ? "0" : "1";
  return [
    `M ${cx} ${cy}`,
    `L ${start.x} ${start.y}`,
    `A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`,
    "Z",
  ].join(" ");
};

const PieChart = ({ data, colors }) => {
  const total = data.reduce((sum, [, val]) => sum + Number(val || 0), 0);
  const size = 480;
  const cx = size / 2;
  const cy = size / 2;
  const radius = 185;

  if (total === 0) {
    return (
      <div
        className="d-flex align-items-center justify-content-center text-muted"
        style={{ width: "100%", minHeight: 280 }}
      >
        No data available
      </div>
    );
  }

  let startAngle = 0;
  const slices = data
    .map(([, val], index) => {
      const value = Number(val || 0);
      if (value <= 0) return null;
      const percent = (value / total) * 100;
      const angle = (value / total) * 360;
      const endAngle = startAngle + angle;
      const midAngle = startAngle + angle / 2;
      const labelPos = polarToCartesian(cx, cy, radius * 0.62, midAngle);
      const isFull = percent >= 99.999;
      const slice = {
        percent,
        isFull,
        path: isFull ? null : describeSlice(cx, cy, radius, startAngle, endAngle),
        color: colors[index] || "#cbd5e1",
        labelPos,
      };
      startAngle = endAngle;
      return slice;
    })
    .filter(Boolean);

  return (
    <svg
      width="100%"
      height="auto"
      viewBox={`0 0 ${size} ${size}`}
      style={{ maxWidth: 520, maxHeight: 520, display: "block", margin: "0 auto" }}
    >
      {slices.map((slice, index) => (
        <g key={index}>
          {slice.isFull ? (
            <circle cx={cx} cy={cy} r={radius} fill={slice.color} stroke="#fff" strokeWidth="1.5" />
          ) : (
            <path d={slice.path} fill={slice.color} stroke="#fff" strokeWidth="1.5" />
          )}
          {slice.percent >= 4 && (
            <text
              x={slice.labelPos.x}
              y={slice.labelPos.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#222"
              fontSize="18"
              fontWeight="600"
              style={{ pointerEvents: "none" }}
            >
              {`${slice.percent.toFixed(1)}%`}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
};

const ReportBar = ({ bardata, options, clickData, onClick }) => {
  const graphData = [bardata[0].slice(0, 2)].concat(
    bardata.filter((a) => a[2]).map((a) => a.slice(0, 2))
  );
  const chartData = graphData.slice(1);
  const chartColors = options.colors.slice(1);

  const legendData = bardata.slice(1).map(([status, count], index) => ({
    label: status,
    count,
    color: options.colors[index],
    click: onClick,
  }));

  return (
    <div
      className="d-flex flex-column flex-sm-row align-items-center mb-2 w-100"
      style={{ gap: "1rem" }}
    >
      <div className="flex-grow-1 w-100" style={{ minWidth: 0 }}>
        <PieChart data={chartData} colors={chartColors} />
      </div>

      <div
        className="d-flex flex-column justify-content-center align-items-center flex-shrink-0"
        style={{ width: 200 }}
      >
        {legendData.map(({ label, count, color, click }, index) => (
          <CustomBox
            className="my-1 my-sm-3 clickable"
            key={index}
            topColor="#ffffff"
            bottomColor={color}
            onClick={() => click && click({ listmode: clickData[index], label: label })}
          >
            <div>
              <Typography className="label" style={{ width: "140px" }} variant="body2">
                {label}
              </Typography>
              <Typography className="count" variant="body2">
                {count}
              </Typography>
            </div>
          </CustomBox>
        ))}
      </div>
    </div>
  );
};

export default ReportBar;
